import { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { isEmpty } from 'lodash'

import { getFiltersForSearch } from 'services/Utilities'
import { updateSetting, updateLocalSetting } from 'store/actions/settings'
import { MFilters } from 'types/filterTypes'

export type OptionsToSaveType = MFilters.FilterTypes[MFilters.FilterKey] | Record<string, never>

const useUpdateSettings = (
  contentName: MFilters.ContentTypes
): {
  saveOptions: (optionsToSave: OptionsToSaveType, { ignoreLargeCompany }?: { ignoreLargeCompany?: boolean }) => void
  savePresets: (presetsToSave: MFilters.PresetTypes<MFilters.FilterKey>) => void
} => {
  const dispatch = useDispatch()
  const { isLargeCompany } = useSelector((state: MStore.RootState) => state.settings)
  const filterKey: MFilters.FilterKey = `filter-${contentName}`
  const filterParsedKey: MFilters.FilterParsedKey = `search-content-parsed-${contentName}`
  const presetKey: MFilters.PresetKey = `saved-search-${contentName}`

  const cleanFilters = (optionsToSave: OptionsToSaveType): OptionsToSaveType => {
    return Object.keys(optionsToSave).reduce((acc, key) => {
      const isArray = Array.isArray(optionsToSave[key])
      const isArrayEmpty = isArray && isEmpty(optionsToSave[key])
      if (!isArrayEmpty && key) acc[key] = optionsToSave[key]
      return acc
    }, {})
  }

  const saveOptions = useCallback(
    (optionsToSave: OptionsToSaveType, { ignoreLargeCompany = false }: { ignoreLargeCompany?: boolean } = {}) => {
      const rangeStart = 'rangeStart' in optionsToSave ? optionsToSave.rangeStart : undefined
      const rangeEnd = 'rangeEnd' in optionsToSave ? optionsToSave.rangeEnd : undefined

      const cleanedFilters = { ...cleanFilters(optionsToSave), rangeStart, rangeEnd }

      // If large company, save the filters in the local storage the user clicks on the search button
      if (!ignoreLargeCompany && isLargeCompany) {
        dispatch(updateLocalSetting(filterKey, cleanedFilters))
      } else {
        dispatch(updateSetting(filterKey, cleanedFilters))
        dispatch(updateSetting(filterParsedKey, getFiltersForSearch(cleanedFilters)))
      }
    },
    [dispatch, filterKey, filterParsedKey, isLargeCompany]
  )

  const savePresets = useCallback(
    (presetsToSave: MFilters.PresetTypes<MFilters.FilterKey>) => {
      dispatch(updateSetting(presetKey, presetsToSave))
    },
    [dispatch, presetKey]
  )

  return { saveOptions, savePresets }
}

export default useUpdateSettings
